import type { CalendarPaymentItem } from '#app/hooks/queries/usePaymentsAllQuery/types';
import type { CalendarCouponPayment } from '#app/pages/Relations/CouponDeal/types/couponDealForm';
import { activePaymentRelationChannel } from '#app/constants/activePaymentRelationTypes';
import { parseCouponDealRecordId } from '#app/pages/Relations/CouponDeal/utils/parseCouponDealRecordId';
import { resolveCalendarPaymentRecordId } from '#app/pages/Relations/CouponDeal/utils/resolveCalendarPaymentRecordId';
import { normalizeActivePayoutDate } from '#app/pages/Investing/Calendar/utils/activePayoutDate';
import { getPaymentCatalogHubId } from '#app/pages/Investing/Calendar/utils/getPaymentCatalogHubId';

function buildInstrumentAggregatePart(payment: CalendarPaymentItem): string | null {
  const isin = String(payment.isin ?? '')
    .trim()
    .toUpperCase();
  if (isin) return `isin:${isin}`;

  const ticker = String(payment.ticker ?? '')
    .trim()
    .toUpperCase();
  if (ticker && !ticker.startsWith('CATALOG.')) return `ticker:${ticker}`;

  const hubId = getPaymentCatalogHubId(payment);
  if (hubId != null) return `hub:${hubId}`;

  if (ticker) return `ticker:${ticker}`;
  return null;
}

export type PaymentAggregateBucket = 'fact' | 'plan';

export function getPaymentAggregateBucket(payment: CalendarPaymentItem): PaymentAggregateBucket {
  const matched = payment.matchedPayout;
  if (matched?.source === 'active' && matched.allowEdit) return 'fact';
  if (
    resolveCalendarPaymentRecordId(payment as CalendarCouponPayment) != null &&
    (Number(payment.sum) || 0) > 0
  ) {
    return 'fact';
  }
  return 'plan';
}

/** Ключ группы: дата + тип выплаты + валюта + инструмент (hub или ISIN). */
export function buildPaymentAggregateKey(payment: CalendarPaymentItem): string | null {
  const date =
    normalizeActivePayoutDate(payment.date) ??
    normalizeActivePayoutDate((payment.raw as { date?: string } | undefined)?.date);
  if (!date) return null;

  const kind = payment.payment_type === 'coupon' ? 'coupon' : 'dividend';
  const currencyId = payment.currency_id ?? 1;
  const bucket = getPaymentAggregateBucket(payment);
  const relationType =
    payment.relation_type ??
    (payment.raw as { relation_type?: string } | undefined)?.relation_type ??
    null;
  const channel = activePaymentRelationChannel(relationType) ?? 'legacy';
  const inst = buildInstrumentAggregatePart(payment);
  if (inst) return `${bucket}|${date}|${kind}|${inst}|cur:${currencyId}|${channel}`;

  return null;
}

function memberRecordId(member: CalendarPaymentItem): number | null {
  const fromPayout = parseCouponDealRecordId(member.matchedPayout?.id);
  if (fromPayout != null) return fromPayout;
  return resolveCalendarPaymentRecordId(member as CalendarCouponPayment);
}

function collectGroupedRecordIds(members: CalendarPaymentItem[]): number[] {
  const ids = new Set<number>();
  for (const member of members) {
    const grouped = member.groupedRecordIds;
    if (Array.isArray(grouped)) {
      grouped.forEach((id) => {
        const n = Number(id);
        if (Number.isFinite(n)) ids.add(n);
      });
    }
    const single = memberRecordId(member);
    if (single != null) ids.add(single);
  }
  return [...ids].sort((a, b) => a - b);
}

function mergePaymentAggregateGroup(members: CalendarPaymentItem[]): CalendarPaymentItem {
  const primary = members[0];
  const groupedRecordIds = collectGroupedRecordIds(members);
  const aggregateKey = buildPaymentAggregateKey(primary);

  const sum = members.reduce((acc, m) => acc + (Number(m.sum) || 0), 0);
  const planSum = members.reduce((acc, m) => acc + (Number(m.plan_sum) || 0), 0);
  const originalSum = members.reduce((acc, m) => acc + (Number(m.original_sum) || 0), 0);
  const originalPlanSum = members.reduce((acc, m) => acc + (Number(m.original_plan_sum) || 0), 0);

  const id =
    groupedRecordIds.length === 1
      ? String(groupedRecordIds[0])
      : aggregateKey
        ? `grouped-${aggregateKey}`
        : primary.id;

  return {
    ...primary,
    id,
    date: primary.date,
    sum,
    plan_sum: planSum || null,
    original_sum: originalSum || sum,
    original_plan_sum: originalPlanSum || undefined,
    groupedMembers: members,
    ...(groupedRecordIds.length > 1 ? { groupedRecordIds } : {}),
  };
}

/**
 * Одна строка в списке/календаре на ISIN (или catalog_hub) и дату с суммой по всем долям.
 */
export function aggregatePaymentsByInstrumentAndDate(
  payments: CalendarPaymentItem[]
): CalendarPaymentItem[] {
  const groups = new Map<string, CalendarPaymentItem[]>();
  const standalone: CalendarPaymentItem[] = [];

  for (const payment of payments) {
    const key = buildPaymentAggregateKey(payment);
    if (!key) {
      standalone.push(payment);
      continue;
    }
    const list = groups.get(key) ?? [];
    list.push(payment);
    groups.set(key, list);
  }

  const result: CalendarPaymentItem[] = [...standalone];
  for (const members of groups.values()) {
    result.push(members.length === 1 ? members[0] : mergePaymentAggregateGroup(members));
  }

  return result;
}

export function parseGroupedPaymentRecordIds(payment: CalendarPaymentItem): number[] {
  if (Array.isArray(payment.groupedRecordIds) && payment.groupedRecordIds.length > 0) {
    return payment.groupedRecordIds
      .map((id) => parseCouponDealRecordId(id))
      .filter((id): id is number => id != null);
  }
  const single = resolveCalendarPaymentRecordId(payment as CalendarCouponPayment);
  return single != null ? [single] : [];
}
